import React, { useState, useEffect, useMemo } from "react";
import api from "../../../../api"; // Using the imported api client
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";

import "swiper/css/pagination";
import "swiper/css";

import "./CodeOfConduct.css";

import DynamicBanner from "../../include/DynamicBanner";
import MarqueeSection from "./Partials/MarqueeSection";

// --- API Endpoints ---
const COC_API_URL = "api/sitepages/about-us/code-of-conduct";
const COE_API_URL = "api/sitepages/about-us/code-of-ethics";
// --- Static Library Images Array ---
const staticLibraryImages = [
  {
    id: 1,
    src: "https://care.edu.in/wp-content/uploads/2020/09/library3.jpg",
    alt: "Library 1",
  },
  {
    id: 2,
    src: "https://care.edu.in/wp-content/uploads/2020/10/library6.jpg",
    alt: "Library 2",
  },
];

const staticLibraryRules = {
  content_intro: [
    "Students are to produce their Identity Card at the entrance and record their name and department in the register provided and also record their entry and exit on the biometric attendance in the library.",
    "Students should be dressed in formal wear while using the library.",
    "Library users are to maintain silence and appropriate behaviour to ensure a conducive learning environment.",
    "Smoking, refreshments and cell phones are strictly prohibited inside the library.",
  ],
  readmore_content: [
    "Personal books are allowed on the II floor: Time: 8.30.am to 4.30pm. Photocopied and printed loose papers are not allowed to bring for study inside the library.",
    "Bags and personal belongings should be kept in the cubicles at the entrance.",
    "Do not rearrange the furniture.",
    "Students are requested to take the initiative of notifying the librarian regarding defects found in books and bound volumes.",
  ],
};

// --- Utility Functions ---

// Helper to extract nested data from the 'groups' array structure
const normalizeApiData = (groups, key) => {
  const group = groups.find((g) => g[key]);
  return group ? group[key] : null;
};

// Helper to extract nested content blocks (like Students/Faculty)
const normalizeContentBlocks = (groups, groupKey, blockKey) => {
  const group = normalizeApiData(groups, groupKey);
  if (group && group.blocks) {
    return group.blocks.find(
      (block) => block.heading.toUpperCase() === blockKey.toUpperCase(),
    );
  }
  return null;
};

// Helper to construct the full image URL
const getImageUrl = (path, fallback = "") => {
  if (path && path.startsWith("/storage")) {
    // Use REACT_APP_API_URL prefix for storage paths
    return `${process.env.REACT_APP_API_URL}/public${path}`;
  }
  // Use the path directly for external URLs or local assets
  return path || fallback;
};

const CodeOfConduct = () => {
  const [activeTab, setActiveTab] = useState("codeofconduct");
  const [cocData, setCocData] = useState(null);
  const [coeData, setCoeData] = useState(null);
  const [banner, setBanner] = useState({
    title: "Code of Conduct",
    desktop_images: [],
    mobile_images: [],
  });
  const [loading, setLoading] = useState(true);

  // --- State for Internal Tabs/Sections ---
  const [activeCommitteeTab, setActiveCommitteeTab] = useState("committee1");
  const [activeEthicsTab, setActiveEthicsTab] = useState(""); // Initialized to empty string
  const [currentStep, setCurrentStep] = useState(1);
  const [expandedSections, setExpandedSections] = useState({});

  const toggleReadMore = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // --- Data Fetching Effect (Using imported 'api') ---
  useEffect(() => {
    const fetchCocData = async () => {
      try {
        const response = await api.get(COC_API_URL); // Using api.get
        const data = response.data;
        setCocData(data.groups);
        setBanner({
          title: data.banner_data.title,
          desktop_images: data.banner_data.desktop_images,
          mobile_images: data.banner_data.mobile_images,
        });
      } catch (error) {
        console.error("Error fetching Code of Conduct data:", error);
      }
    };

    const fetchCoeData = async () => {
      try {
        const response = await api.get(COE_API_URL); // Using api.get
        setCoeData(response.data.groups);
      } catch (error) {
        console.error("Error fetching Code of Ethics data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCocData();
    fetchCoeData();
  }, []);

  // --- Data Mapping & Memoization ---
  const marqueeSection = useMemo(
    () => (cocData ? normalizeApiData(cocData, "marquee_section") : null),
    [cocData],
  );
  const committeeData = useMemo(
    () => (cocData ? normalizeApiData(cocData, "committee_data") : null),
    [cocData],
  );
  const employeeConduct = useMemo(
    () => (cocData ? normalizeApiData(cocData, "employee_conduct") : null),
    [cocData],
  );
  const examRules = useMemo(
    () => (cocData ? normalizeApiData(cocData, "exam_rules") : null),
    [cocData],
  );
  const hostelRulesData = useMemo(
    () => (cocData ? normalizeApiData(cocData, "hostel_rules") : null),
    [cocData],
  );
  const studentBlock = useMemo(
    () =>
      cocData
        ? normalizeContentBlocks(cocData, "student_faculty_conduct", "STUDENTS")
        : null,
    [cocData],
  );
  const facultyBlock = useMemo(
    () =>
      cocData
        ? normalizeContentBlocks(cocData, "student_faculty_conduct", "FACULTY")
        : null,
    [cocData],
  );

  const coeIntro = useMemo(
    () => (coeData ? normalizeApiData(coeData, "intro_section") : null),
    [coeData],
  );
  const coeCoreRules = useMemo(
    () => (coeData ? normalizeApiData(coeData, "core_ethics_rules") : null),
    [coeData],
  );
  const coeTabsData = useMemo(
    () => (coeData ? normalizeApiData(coeData, "ethics_tabs") : null),
    [coeData],
  );
  const coeWellbeing = useMemo(
    () => (coeData ? normalizeApiData(coeData, "wellbeing_section") : null),
    [coeData],
  );
  const coeDedication = useMemo(
    () => (coeData ? normalizeApiData(coeData, "dedication_section") : null),
    [coeData],
  );

  const ethicsTabs = useMemo(() => coeTabsData?.tabs || [], [coeTabsData]);
  const activeEthicsTabContent = useMemo(
    () => ethicsTabs.find((tab) => tab.id === activeEthicsTab),
    [ethicsTabs, activeEthicsTab],
  );

  // Prepare faculty rules for the toggle animation
  const facultyRulesForAnimation = useMemo(() => {
    if (facultyBlock) {
      const facultyRulesContent = facultyBlock.content_intro || "";
      const facultyRulesReadmore = facultyBlock.readmore_content || "";

      // Combine all rules
      const allFacultyRules = facultyRulesContent
        .split("\r\n")
        .filter((p) => p.trim())
        .concat(facultyRulesReadmore.split("\r\n").filter((p) => p.trim()));

      // Split into two halves for animation
      const firstHalfRules = allFacultyRules.slice(
        0,
        Math.ceil(allFacultyRules.length / 2),
      );
      const secondHalfRules = allFacultyRules.slice(
        Math.ceil(allFacultyRules.length / 2),
      );

      return { firstHalfRules, secondHalfRules, allFacultyRules };
    }

    // Fallback if no faculty data
    const fallbackRules = [
      "A teacher is constantly under the scrutiny of his students and the society at large.",
      "CARE expects the following code of conduct from the Faculty.",
      "Teachers try to see through teachers' bodies and organizations, that institutions maintain contact with the guardians, their students.",
      "Send reports of their performance to the guardians whenever necessary.",
      "Meet them in meetings convened for the purpose of mutual exchange of ideas.",
      "Teachers should also refrain from inciting students against other students, colleagues or administration.",
    ];

    return {
      firstHalfRules: fallbackRules.slice(0, 3),
      secondHalfRules: fallbackRules.slice(3),
      allFacultyRules: fallbackRules,
    };
  }, [facultyBlock]);

  // 🌟 FIX: Set initial active tab for Code of Ethics after data loads
  useEffect(() => {
    if (
      activeTab === "codeofethics" &&
      ethicsTabs.length > 0 &&
      !activeEthicsTab
    ) {
      setActiveEthicsTab(ethicsTabs[0].id);
    }
  }, [activeTab, ethicsTabs, activeEthicsTab]);

  const SliderSettings = {
    className: "mySwiper",
    spaceBetween: 10,
    slidesPerView: 1,
    modules: [Pagination, Autoplay],
    autoplay: true,
    loop: true,
  };
  const sliderSettings2 = {
    spaceBetween: 30,
    slidesPerView: 1,
    modules: [Autoplay, Pagination],
    autoplay: { delay: 3000, disableOnInteraction: false },
    loop: true,
  };
  const sliderSettings3 = {
    className: "h-auto",
    spaceBetween: 20,
    slidesPerView: 1,
  };

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  // --- Dynamic Render Logic (COC Tab) ---
  const renderConductContent = () => {
    return (
      <div className="tab-pane fade show active">
        {/* Students Section */}
        <section>
          <div className="container">
            <div className="sec-title py-4 m-0">
              <h2>{studentBlock?.heading || "STUDENTS"}</h2>
            </div>
            <div className="mb-3 mb-md-5">
              <div className="row align-items-center d-flex justify-content-centrer">
                {/* --- Image (Applying getImageUrl) --- */}
                <div
                  className={`col-md-5 col-12 text-center mb-3 mb-md-0 ${studentBlock?.image_position === "right" ? "order-1 order-md-2" : "order-1"}`}
                >
                  <img
                    src={getImageUrl(
                      studentBlock?.image_url,
                      "https://caredemo.care.edu.in/assets/images/coc4.webp",
                    )}
                    alt="Students"
                    className="img-fluid rounded"
                  />
                </div>

                {/* --- Text Content --- */}
                <div
                  className={`col-md-7 col-12 ${studentBlock?.image_position === "right" ? "order-2 order-md-1" : "order-2"}`}
                >
                  {/* Desktop View */}
                  <div className="d-none d-md-block">
                    <p>
                      {studentBlock?.content_intro ||
                        "This Code of Conduct shall apply to all kinds of conduct..."}
                    </p>
                    <p>
                      {studentBlock?.readmore_content ||
                        "Dress code does not necessarily mean college uniform..."}
                    </p>
                  </div>
                  {/* Mobile View (with Read More) */}
                  <div className="d-block d-md-none">
                    <p>
                      {studentBlock?.content_intro ||
                        "This Code of Conduct shall apply to all kinds of conduct..."}
                    </p>
                    {expandedSections.students && (
                      <p>
                        {studentBlock?.readmore_content ||
                          "Dress code does not necessarily mean college uniform..."}
                      </p>
                    )}
                    {studentBlock?.readmore_content?.length > 0 && (
                      <button
                        className="readmoreall mb-3 d-inline-block"
                        onClick={() => toggleReadMore("students")}
                      >
                        <span className="txt">
                          {expandedSections.students
                            ? "Read Less"
                            : "Read More"}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Committee Tabs Section */}
        <section className="mt-3 mt-md-5">
          <div className="container">
            <ul className="nav nav-tabs code mb-3 d-flex justify-content-center align-items-center">
              {committeeData?.tabs?.map((tab, index) => (
                <li className="nav-item" key={index}>
                  <button
                    className={`nav-link ${activeCommitteeTab === `committee${index + 1}` ? "active" : ""}`}
                    onClick={() =>
                      setActiveCommitteeTab(`committee${index + 1}`)
                    }
                  >
                    {tab.title}
                  </button>
                </li>
              ))}
            </ul>

            <div className="tab-content-coc code-content-table">
              {/* Monitoring Committee */}
              {activeCommitteeTab === "committee1" &&
                committeeData?.tabs[0] && (
                  <div className="tab-pane fade show active">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            {committeeData.tabs[0].table.cols.map((col, i) => (
                              <th key={i}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {committeeData.tabs[0].table.rows.map(
                            (row, index) => (
                              <tr key={index}>
                                {row.map((cell, i) => (
                                  <td key={i}>
                                    {i === 1 ? <strong>{cell}</strong> : cell}
                                  </td>
                                ))}
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* Dress Code */}
              {activeCommitteeTab === "committee2" &&
                committeeData?.tabs[1] && (
                  <div className="tab-pane fade show active">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            {committeeData.tabs[1].table.cols.map((col, i) => (
                              <th key={i}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Dress Codes table */}
                          {committeeData.tabs[1].table.rows.map(
                            (row, index) => (
                              <tr key={index}>
                                {/* FIRST COLUMN – STRONG */}
                                <td>
                                  <strong>{row[0]}</strong>
                                </td>

                                {/* SECOND COLUMN – NORMAL TEXT */}
                                <td>
                                  {row[1].split("\r\n").map((line, i) => (
                                    <p key={i} className="mb-0">
                                      {line}
                                    </p>
                                  ))}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </section>

        {/* Library & Examination Rules Section */}

        <section className="library-section ">
          <div className="container">
            <div className="sec-title">
              <h2>{examRules?.heading || "Library & Examination Rules"}</h2>
            </div>
            <div className="subsection ">
              <h4 className="text-start">Library Rules</h4>

              {/* DESKTOP VIEW WITH READ MORE */}
              <div className="row d-none d-md-flex align-items-center justify-content-center ">
                <div className="col-md-7 col-12">
                  <ul className="ps-0 m-0">
                    <li className="d-flex align-items-start flex-column">
                      {/* Displaying static content for Library rules in bullet points */}
                      {/* In your Library section */}
                      <ul className="library-rules-list ps-3">
                        {/* Intro content */}
                        {Array.isArray(staticLibraryRules.content_intro)
                          ? staticLibraryRules.content_intro.map((rule, i) => (
                              <li key={`intro-${i}`} className="rule-text mb-2">
                                <i class="fa fa-book me-2"></i>
                                {rule}
                              </li>
                            ))
                          : staticLibraryRules.content_intro
                              .split("\r\n")
                              .filter((p) => p.trim())
                              .map((rule, i) => (
                                <li
                                  key={`intro-${i}`}
                                  className="rule-text mb-2"
                                >
                                  <i class="fa fa-book me-2"></i>
                                  {rule}
                                </li>
                              ))}

                        {/* Read more content */}
                        {expandedSections.library &&
                          (Array.isArray(staticLibraryRules.readmore_content)
                            ? staticLibraryRules.readmore_content.map(
                                (rule, i) => (
                                  <li
                                    key={`readmore-${i}`}
                                    className="rule-text mb-2"
                                  >
                                    <i class="fa fa-book me-2"></i>
                                    {rule}
                                  </li>
                                ),
                              )
                            : staticLibraryRules.readmore_content
                                .split("\r\n")
                                .filter((p) => p.trim())
                                .map((rule, i) => (
                                  <li
                                    key={`readmore-${i}`}
                                    className="rule-text mb-2"
                                  >
                                    <i class="fa fa-book me-2"></i>
                                    {rule}
                                  </li>
                                )))}
                      </ul>

                      {/* Read More/Less button for desktop */}
                      {staticLibraryRules.readmore_content?.length > 0 && (
                        <button
                          className="readmoreall d-inline-block mt-2"
                          onClick={() => toggleReadMore("library")}
                        >
                          <span className="txt">
                            {expandedSections.library
                              ? "Read Less"
                              : "Read More"}
                          </span>
                        </button>
                      )}
                    </li>
                  </ul>
                </div>

                {/* RIGHT SWIPER IMAGES - Using static images array */}
                <div className="col-md-5 col-12">
                  <Swiper
                    modules={[Autoplay]}
                    autoplay={{ delay: 2500, disableOnInteraction: false }}
                    pagination={false}
                    loop={true}
                    spaceBetween={20}
                    slidesPerView={1}
                    className="library-swiper"
                  >
                    {staticLibraryImages.map((img, index) => (
                      <SwiperSlide key={index}>
                        <img
                          src={img.src}
                          alt={img.alt}
                          className="img-fluid rounded-3"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>

              {/* MOBILE VIEW */}
              <div className="row d-block d-md-none mt-4">
                <div className="col-12">
                  <div>
                    {/* Library rules in bullet points for mobile */}
                    {/* In your Library section */}
                    <ul className="library-rules-list ps-3">
                      {/* Intro content */}
                      {Array.isArray(staticLibraryRules.content_intro)
                        ? staticLibraryRules.content_intro.map((rule, i) => (
                            <li key={`intro-${i}`} className="rule-text mb-2">
                              <i class="fa fa-book me-2"></i>
                              {rule}
                            </li>
                          ))
                        : staticLibraryRules.content_intro
                            .split("\r\n")
                            .filter((p) => p.trim())
                            .map((rule, i) => (
                              <li key={`intro-${i}`} className="rule-text mb-2">
                                <i class="fa fa-book me-2"></i>
                                {rule}
                              </li>
                            ))}

                      {/* Read more content */}
                      {expandedSections.library &&
                        (Array.isArray(staticLibraryRules.readmore_content)
                          ? staticLibraryRules.readmore_content.map(
                              (rule, i) => (
                                <li
                                  key={`readmore-${i}`}
                                  className="rule-text mb-2"
                                >
                                  <i class="fa fa-book me-2"></i>
                                  {rule}
                                </li>
                              ),
                            )
                          : staticLibraryRules.readmore_content
                              .split("\r\n")
                              .filter((p) => p.trim())
                              .map((rule, i) => (
                                <li
                                  key={`readmore-${i}`}
                                  className="rule-text mb-2"
                                >
                                  <i class="fa fa-book me-2"></i>
                                  {rule}
                                </li>
                              )))}
                    </ul>

                    {/* Read More/Less button for mobile */}
                    {staticLibraryRules.readmore_content?.length > 0 && (
                      <button
                        className="readmoreall mt-3 mb-3 d-inline-block"
                        onClick={() => toggleReadMore("library")}
                      >
                        <span className="txt">
                          {expandedSections.library ? "Read Less" : "Read More"}
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Swiper for mobile - Using static images array */}
                <div className="col-12 mt-3">
                  <Swiper
                    modules={[Autoplay, Pagination]}
                    autoplay={{ delay: 2500, disableOnInteraction: false }}
                    pagination={{ clickable: true }}
                    loop={true}
                    spaceBetween={15}
                    slidesPerView={1}
                    className="library-swiper-mobile"
                  >
                    {staticLibraryImages.map((img, i) => (
                      <SwiperSlide key={i}>
                        <img
                          src={img.src}
                          alt={img.alt}
                          className="img-fluid rounded-3"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Examination Rules Section */}
        <section>
          <div className="container">
            <div className="sec-title py-5">
              <h2> Examination Rules</h2>
            </div>
            <div className="exam-info-banner">
              <div className="row d-flex justify-content-center align-items-center">
                <div className="col-md-3 col-12 d-flex justify-content-center align-items-center">
                  <div className="exam-img d-flex align-items-center mb-3">
                    {/* Assuming this image is a static asset or base URL image */}
                    <img
                      src="/assets/images/warning1.webp"
                      alt="Alert Icon"
                      className="inline-icon me-2"
                    />
                  </div>
                </div>
                <div className="col-md-9 col-12">
                  <h4 style={{ color: "white" }} className="mb-3">
                    {examRules?.exam_banner_heading ||
                      "Important Examination Guidelines"}
                  </h4>
                  {/* Desktop View */}
                  <div className="d-none d-md-block">
                    {(examRules?.exam_banner_content || "")
                      .split("\r\n")
                      .filter((p) => p.trim())
                      .map((p, i) => (
                        <p key={i} className="exam-text">
                          {p}
                        </p>
                      ))}
                    {(examRules?.exam_banner_readmore || "")
                      .split("\r\n")
                      .filter((p) => p.trim())
                      .map((p, i) => (
                        <p key={i} className="exam-text">
                          {p}
                        </p>
                      ))}
                  </div>
                  {/* Mobile View */}
                  <div className="d-block d-md-none">
                    <p className="exam-text">
                      {examRules?.exam_banner_content || ""}
                    </p>
                    {expandedSections.exam &&
                      (examRules?.exam_banner_readmore || "")
                        .split("\r\n")
                        .filter((p) => p.trim())
                        .map((p, i) => (
                          <p key={i} className="exam-text">
                            {p}
                          </p>
                        ))}
                    {examRules?.exam_banner_readmore?.length > 0 && (
                      <button
                        className="readmoreall d-inline-block"
                        onClick={() => toggleReadMore("exam")}
                      >
                        <span className="txt">
                          {expandedSections.exam ? "Read Less" : "Read More"}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hostel Rules Section */}
        <section className="mb-3 mb-md-0">
          <div className="container">
            <div className="sec-title py-5">
              <h2>{hostelRulesData?.heading || "Hostel Rules"}</h2>
            </div>
            <div className="row d-flex justify-content-center align-items-center">
              <div className="col-md-7 col-12">
                <div>
                  <ul className="ps-0 m-0">
                    {hostelRulesData?.rules?.map((rule, index) => (
                      <li key={index} className="d-flex align-items-start mb-3">
                        <i className="fa fa-bed rule-icon me-2 mt-1"></i>
                        <p className="rule-text mb-0">{rule}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="col-md-5 col-12">
                <div>
                  <img
                    src={getImageUrl(
                      hostelRulesData?.image_url,
                      "/assets/images/hostel1.webp",
                    )}
                    alt="Hostel"
                    className="img-fluid"
                    style={{
                      borderRadius: "10px",
                      height: "300px",
                      width: "100%",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Faculty Section (Rules Toggle) */}
        <section className="content-section ">
          <div className="container">
            <div className="sec-title py-5">
              <h2>{facultyBlock?.heading || "Faculty"}</h2>
            </div>

            <div className="row g-0 d-flex justify-content-center align-items-center">
              {/* Image must be constructed using getImageUrl */}
              {facultyBlock?.image_url && (
                <div className="col-md-5 col-12 text-center">
                  <img
                    src={getImageUrl(
                      facultyBlock.image_url,
                      "/assets/images/campus-edited.webp",
                    )}
                    alt="Faculty"
                    className="img-fluid circle-img mb-4"
                  />
                </div>
              )}
              <div className="col-md-7 col-12">
                <div className="content-card position-relative">
                  {/* Faculty Content with Read More instead of arrow toggle */}
                  <div className="faculty-content">
                    {/* FIRST CONTENT (Always visible) */}
                    <div className="content-item visible" id="firstContent">
                      <ul className="ps-0 m-0">
                        {facultyRulesForAnimation.firstHalfRules.map(
                          (rule, index) => (
                            <li
                              key={index}
                              className="d-flex align-items-start mb-3"
                            >
                              <i className="fa fa-user rule-icon me-2 mt-1"></i>
                              <p className="rule-text mb-0">{rule}</p>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>

                    {/* SECOND CONTENT (Visible when Read More is clicked) */}
                    {expandedSections.faculty &&
                      facultyRulesForAnimation.secondHalfRules.length > 0 && (
                        <div
                          className="content-item visible"
                          id="secondContent"
                        >
                          <ul className="ps-0 m-0">
                            {facultyRulesForAnimation.secondHalfRules.map(
                              (rule, index) => (
                                <li
                                  key={`second-${index}`}
                                  className="d-flex align-items-start mb-3"
                                >
                                  <i className="fa fa-user rule-icon me-2 mt-1"></i>
                                  <p className="rule-text mb-0">{rule}</p>
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}

                    {/* READ MORE BUTTON (replaces toggle arrow) */}
                    {facultyRulesForAnimation.allFacultyRules.length >
                      facultyRulesForAnimation.firstHalfRules.length && (
                      <button
                        className="readmoreall mt-3 d-inline-block"
                        onClick={() => toggleReadMore("faculty")}
                      >
                        <span className="txt">
                          {expandedSections.faculty ? "Read Less" : "Read More"}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Employee Code of Conduct Section */}
        <section>
          <div className="container">
            <div className="sec-title py-5 ">
              <h2>{employeeConduct?.heading || "Employee Code of Conduct"}</h2>
            </div>
            <div className="header">
              <p>
                {employeeConduct?.intro_text ||
                  "Any act of misconduct or breach of discipline..."}
              </p>
            </div>

            {/* Cards Row - Desktop */}
            <div className="row g-4 d-none d-md-flex">
              {employeeConduct?.cards?.map((item, index) => (
                <div key={index} className="col-md-4 col-12">
                  <div className={`card ${item.type}-card`}>
                    <div className="card-img-top-coc">
                      <i className={`card-icon fa ${item.icon}`}></i>
                    </div>
                    <div className="card-body">
                      <h3 className="card-title-code">{item.title}</h3>
                      {item.items?.length > 0
                        ? item.items.map((subItem, subIndex) => (
                            <div key={subIndex} className="warning-item">
                              {subItem}
                            </div>
                          ))
                        : item.content
                            ?.split("\r\n")
                            .filter((p) => p.trim())
                            .map((paragraph, pIndex) => (
                              <p key={pIndex}>{paragraph}</p>
                            ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Swiper */}
            <div className="d-block d-md-none">
              <Swiper {...SliderSettings}>
                {employeeConduct?.cards?.map((item, index) => (
                  <SwiperSlide key={index}>
                    <div className={`card ${item.type}-card`}>
                      <div className="card-img-top-coc">
                        <i className={`card-icon fa ${item.icon}`}></i>
                      </div>
                      <div className="card-body">
                        <h3 className="card-title-code">{item.title}</h3>
                        {item.items?.length > 0
                          ? item.items.map((subItem, subIndex) => (
                              <div key={subIndex} className="warning-item">
                                {subItem}
                              </div>
                            ))
                          : item.content
                              ?.split("\r\n")
                              .filter((p) => p.trim())
                              .map((paragraph, pIndex) => (
                                <p key={pIndex}>{paragraph}</p>
                              ))}
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // --- Dynamic Render Logic (COE Tab) ---
  const renderEthicsContent = () => (
    <div className="tab-pane fade show active">
      {/* Ethics Introduction, Excellence, Scope, Law Alert - Using dynamic data */}
      <section>
        <div className="container">
          <div className="ethics-card mt-3 mt-md-5">
            <p className="lead-paragraph">
              {coeIntro?.intro_paragraph ||
                "The following Code of Ethics must be read..."}
            </p>

            <p>CARE is dedicated to excellence in the following areas:</p>

            {/* Excellence Grid - Desktop */}
            <div className="excellence-grid d-none d-md-flex">
              {coeIntro?.excellence_cards?.map((item, index) => (
                <div className="excellence-item" key={index}>
                  <div className="excellence-icon">
                    <i className={`fa ${item.icon_class}`}></i>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.content}</p>
                </div>
              ))}
            </div>

            {/* Excellence Swiper - Mobile */}
            <div className="d-block d-md-none mt-4">
              <Swiper className="excellence-swiper" {...sliderSettings2}>
                {coeIntro?.excellence_cards?.map((item, index) => (
                  <SwiperSlide key={index}>
                    <div className="excellence-item text-center p-3">
                      <div className="excellence-icon">
                        <i className={`fa ${item.icon_class}`}></i>
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.content}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <p>
              {coeIntro?.after_card_paragraph ||
                "Both the faculty and its students are required to follow this Code of Ethics..."}
            </p>

            {/* Scope of Application */}
            <h2 className="mt-3 mb-3">
              {coeCoreRules?.scope_section?.heading || "Scope of Application"}
            </h2>

            {/* Scope Grid - Desktop */}
            <div className="excellence-grid d-none d-md-flex">
              {coeCoreRules?.scope_section?.cards?.map((item, index) => (
                <div className="excellence-item1" key={index}>
                  <div className="excellence-icon">
                    <i className="fa fa-check-square-o"></i>
                  </div>
                  <p>{item.content}</p>
                </div>
              ))}
            </div>
            {/* Scope Swiper - Mobile */}
            <div className="d-block d-md-none ethics-swiper mt-4">
              <Swiper {...sliderSettings3}>
                {coeCoreRules?.scope_section?.cards?.map((item, index) => (
                  <SwiperSlide
                    className="excellence-item1 text-center p-3"
                    key={index}
                  >
                    <div className="excellence-icon">
                      <i className="fa fa-check-square-o"></i>
                    </div>
                    <p>{item.content}</p>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div className="alert law">
              <i className="fa fa-balance-scale me-2"></i> **
              {coeIntro?.law_alert_heading ||
                "When ethics and law appear to be in conflict,"}
              ** {coeIntro?.law_alert_content || "one should seek counsel..."}
            </div>
          </div>
        </div>
      </section>

      {/* Care of Patients Section */}
      <section>
        <div className="container">
          <div className="sec-title py-4 ">
            <h2>
              <i className="fa fa-heartbeat rule-icon me-2 mt-1"></i>
              {coeCoreRules?.patient_care_section?.heading ||
                "Care of Patients"}
            </h2>
          </div>
          <div className="row d-flex justify-content-center align-items-center">
            <div className="col-md-7 col-12">
              <div>
                <ul className="ps-0 m-0">
                  <li className="d-flex align-items-start flex-column mb-3">
                    <div className="d-flex w-100">
                      <div>
                        {/* Intro Content */}
                        {(
                          coeCoreRules?.patient_care_section?.intro_content ||
                          ""
                        )
                          .split("\r\n")
                          .filter((p) => p.trim())
                          .map((p, i) => (
                            <p key={i} className="rule-text mb-2 ">
                              {p}
                            </p>
                          ))}

                        {/* Read More Content */}
                        {expandedSections.patientCare &&
                          (
                            coeCoreRules?.patient_care_section
                              ?.readmore_content || ""
                          )
                            .split("\r\n")
                            .filter((p) => p.trim())
                            .map((p, i) => (
                              <p key={i} className="mt-2">
                                {p}
                              </p>
                            ))}
                        <br />
                        {coeCoreRules?.patient_care_section?.readmore_content
                          ?.length > 0 && (
                          <button
                            className="readmoreall mt-3 mt-md-3 d-inline-block"
                            onClick={() => toggleReadMore("patientCare")}
                          >
                            <span className="txt">
                              {expandedSections.patientCare
                                ? "Read Less"
                                : "Read More"}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-md-5 col-12">
              <div className="code-image">
                <img
                  src={getImageUrl(
                    coeCoreRules?.patient_care_section?.image_url,
                    "/assets/images/physiotherap.jpg",
                  )}
                  alt="Patient Care"
                  className="img-fluid"
                  style={{
                    borderRadius: "10px",
                    height: "100%",
                    width: "100%",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Respect for Humans Section */}
      <section>
        <div className="container">
          <div className="sec-title py-4 ">
            <h2>
              <i className="fa fa-hand-peace-o rule-icon me-2 mt-1"></i>
              {coeCoreRules?.respect_section?.heading || "Respect for Humans"}
            </h2>
          </div>
          <div className="row d-flex justify-content-center align-items-center">
            <div className="col-md-5 col-12 mb-3 d-flex justify-content-center align-items-center">
              <div className="code-image">
                <img
                  src={getImageUrl(
                    coeCoreRules?.respect_section?.image_url,
                    "https://care.edu.in/wp-content/uploads/2020/09/Thumbnail_0000_CCN1.jpg",
                  )}
                  alt="Respect"
                  className="img-fluid"
                  style={{
                    borderRadius: "10px",
                    height: "100%",
                    width: "100%",
                  }}
                />
              </div>
            </div>
            <div className="col-md-7 col-12">
              <div>
                <ul className="ps-0 m-0">
                  {coeCoreRules?.respect_section?.rules?.map((rule, index) => (
                    <li key={index} className="d-flex align-items-start mb-3">
                      <i
                        className={`fa ${rule.icon_class || "fa-hand-peace-o"} rule-icon me-2 mt-1`}
                      ></i>
                      <p className="rule-text ">{rule.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maintain Confidentiality Section */}
      <section>
        <div className="container">
          <div className="sec-title py-4 ">
            <h2>
              <i className="fa fa-lock rule-icon me-2 mt-1"></i>
              {coeCoreRules?.confidentiality_section?.heading ||
                "Maintain Confidentiality"}
            </h2>
          </div>
          <div className="row d-flex justify-content-center align-items-center">
            <div className="col-md-7 col-12">
              <div>
                <ul className="ps-0 m-0">
                  {coeCoreRules?.confidentiality_section?.rules?.map(
                    (rule, index) => (
                      <li key={index} className="d-flex align-items-start mb-3">
                        <i
                          className={`fa ${rule.icon_class || "fa-lock"} rule-icon me-2 mt-1`}
                        ></i>
                        <p className="rule-text mb-0">{rule.text}</p>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>
            <div className="col-md-5 col-12 d-flex justify-content-center align-items-center">
              <div className="code-image">
                <img
                  src={getImageUrl(
                    coeCoreRules?.confidentiality_section?.image_url,
                    "https://care.edu.in/wp-content/uploads/2020/02/images_0002_4Z9A8228.jpg",
                  )}
                  alt="Confidentiality"
                  className="img-fluid"
                  style={{
                    borderRadius: "10px",
                    height: "100%",
                    width: "100%",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ethics Tabs - Desktop */}
      <section className="mb-5 mt-5 d-none d-md-block">
        <div className="container">
          <div className="main-container">
            <div className="side-tabs">
              <ul className="nav nav-tabs code2 mb-3">
                {ethicsTabs.map((tab) => (
                  <li key={tab.id} className="nav-item">
                    <button
                      className={`nav-link ${activeEthicsTab === tab.id ? "active" : ""}`}
                      onClick={() => setActiveEthicsTab(tab.id)}
                    >
                      {tab.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="tab-content-coc2">
              {activeEthicsTabContent && (
                <div className="tab-pane fade show active">
                  {activeEthicsTabContent.sections.map(
                    (section, sectionIndex) => (
                      <section className="mb-5" key={sectionIndex}>
                        <div className="sec-title py-3 ">
                          <h2>{section.heading}</h2>
                        </div>
                        <div className="row d-flex justify-content-center align-items-center">
                          {/* Image on Left for odd sections (sectionIndex % 2 !== 0) */}
                          {section.image_url && sectionIndex % 2 !== 0 && (
                            <div className="col-md-5 col-12 d-flex justify-content-center align-items-center">
                              <img
                                src={getImageUrl(section.image_url)}
                                alt={section.heading}
                                className="img-fluid"
                                style={{
                                  borderRadius: "10px",
                                  height: "230px",
                                  width: "100%",
                                }}
                              />
                            </div>
                          )}
                          {/* Content List */}
                          <div
                            className={
                              section.image_url ? "col-md-7 col-12" : "col-12"
                            }
                          >
                            <div>
                              <ul className="ps-0 m-0">
                                {Object.values(section.rules || {}).map(
                                  (rule, ruleIndex) => (
                                    <li
                                      key={ruleIndex}
                                      className="d-flex align-items-start mb-3"
                                    >
                                      <i
                                        className={`fa ${rule.icon_class ?? "fa-circle"} rule-icon me-2 mt-1`}
                                      ></i>
                                      <p className="rule-text mb-0">
                                        {rule.text ?? ""}
                                      </p>
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          </div>
                          {/* Image on Right for even sections (sectionIndex % 2 === 0) */}
                          {section.image_url && sectionIndex % 2 === 0 && (
                            <div className="col-md-5 col-12 d-flex justify-content-center align-items-center">
                              <img
                                src={getImageUrl(section.image_url)}
                                alt={section.heading}
                                className="img-fluid"
                                style={{
                                  borderRadius: "10px",
                                  height: "230px",
                                  width: "100%",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </section>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Ethics Accordion - Mobile (using same logic as desktop) */}
      <div className="d-md-none d-block mt-5">
        <div className="accordion" id="ethicsAccordion">
          {ethicsTabs.map((tab) => (
            <div key={tab.id} className="ethics-accordion-item">
              <h2 className="ethics-accordion-header">
                <button
                  className={`ethics-accordion-button ${activeEthicsTab === tab.id ? "" : "collapsed"}`}
                  type="button"
                  onClick={() =>
                    setActiveEthicsTab(activeEthicsTab === tab.id ? "" : tab.id)
                  }
                  aria-expanded={activeEthicsTab === tab.id}
                >
                  {tab.title}
                </button>
              </h2>
              <div
                className={`accordion-collapse collapse ${activeEthicsTab === tab.id ? "show" : ""}`}
              >
                <div className="ethics-accordion-body">
                  {activeEthicsTab === tab.id &&
                    tab.sections.map((section, sectionIndex) => (
                      <section key={sectionIndex}>
                        <div className="container">
                          <div className="sec-title ">
                            <h2>{section.heading}</h2>
                          </div>
                          <div className="row d-flex justify-content-center align-items-center">
                            {section.image_url && (
                              <div className="col-12 d-flex justify-content-center align-items-center mb-3">
                                <img
                                  src={getImageUrl(section.image_url)}
                                  alt={section.heading}
                                  className="img-fluid"
                                  style={{ borderRadius: "10px" }}
                                />
                              </div>
                            )}
                            <div className="col-12">
                              <div>
                                <ul className="ps-0 m-0">
                                  {Object.values(section.rules || {}).map(
                                    (rule, ruleIndex) => (
                                      <li
                                        key={ruleIndex}
                                        className="d-flex align-items-start mb-3"
                                      >
                                        <i
                                          className={`fa ${rule.icon_class ?? "fa-circle"} rule-icon me-2 mt-1`}
                                        ></i>
                                        <p className="rule-text mb-0">
                                          {rule.text ?? ""}
                                        </p>
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Personal Well-being Section */}
      <section className="mt-3 mt-md-0">
        <div className="container ">
          <div className="row d-flex justify-content-center align-items-center">
            {coeWellbeing?.cards?.map((card, index) => (
              <React.Fragment key={index}>
                {/* Assuming the first card is the image (Personal Well-being image) */}
                {index === 0 && (
                  <div className="col-md-6 col-12 d-flex">
                    <div className="wellbeing-card w-100">
                      <div className="card-img-container ">
                        <img
                          src={getImageUrl(
                            card.image_url,
                            "https://caredemo.care.edu.in/assets/images/apply-img.jpg",
                          )}
                          alt={card.title || "Well-being"}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {/* Assuming the content for Personal Well-being is in the first card, and Public Well-being is in the dedication section */}
                {index === 0 && (
                  <div className="col-md-6 col-12 d-flex">
                    <div className="wellbeing-card w-100">
                      <div className="card-body d-flex flex-column justify-content-center0 ">
                        <div className="mb-4">
                          <div className="sec-title">
                            <h2>
                              {coeWellbeing?.heading || "Personal Well-being"}
                            </h2>
                          </div>
                          <ul className="guideline-list">
                            {card.content
                              ?.split("\r\n")
                              .filter((p) => p.trim())
                              .map((item, i) => (
                                <li key={i} className="guideline-item">
                                  {item}
                                </li>
                              ))}
                          </ul>
                        </div>
                        {coeDedication?.rules?.length > 0 && (
                          <div>
                            <div className="sec-title pt-0">
                              <h2>
                                {coeDedication?.heading || "Public Well-being"}
                              </h2>
                            </div>
                            <ul className="guideline-list">
                              {coeDedication?.rules?.map((item, index) => (
                                <li key={index} className="guideline-item">
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <>
      <DynamicBanner
        title={banner.title}
        bgImage={banner.desktop_images}
        bgImageMobile={banner.mobile_images}
      />
      {/* Pass marquee data to MarqueeSection */}
      <MarqueeSection marqueeData={marqueeSection} />
      <section className="mt-3 mt-md-5 codeconduct-section">
        <div className="container mb-3 mb-md-5">
          <ul className="nav nav-tabs code-tabs d-flex justify-content-center align-items-center">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "codeofconduct" ? "active" : ""}`}
                onClick={() => setActiveTab("codeofconduct")}
              >
                Code of Conduct
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "codeofethics" ? "active" : ""}`}
                onClick={() => setActiveTab("codeofethics")}
              >
                Code of Ethics
              </button>
            </li>
          </ul>

          {/* Tab Content */}
          <div className="tab-content-coc">
            {activeTab === "codeofconduct" && renderConductContent()}
            {activeTab === "codeofethics" && renderEthicsContent()}
          </div>
        </div>
      </section>
    </>
  );
};

export default CodeOfConduct;
